import React, { createContext, useContext, useEffect, useCallback, useState, useRef, ReactNode } from 'react';

interface Shortcut {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  description: string;
  action: () => void;
}

interface KeyboardShortcutsContextType {
  registerShortcut: (shortcut: Shortcut) => void;
  unregisterShortcut: (key: string) => void;
  shortcuts: Shortcut[];
  showHelp: boolean;
  setShowHelp: (show: boolean) => void;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | undefined>(undefined);

export const KeyboardShortcutsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [showHelp, setShowHelp] = useState(false);

  const registerShortcut = useCallback((shortcut: Shortcut) => {
    setShortcuts(prev => {
      const filtered = prev.filter(s => s.key !== shortcut.key);
      return [...filtered, shortcut];
    });
  }, []);

  const unregisterShortcut = useCallback((key: string) => {
    setShortcuts(prev => prev.filter(s => s.key !== key));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        // Allow Escape even in inputs
        if (e.key !== 'Escape') return;
      }

      const key = e.key.toLowerCase();

      // Special case: ? for help
      if (key === '?' && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setShowHelp(prev => !prev);
        return;
      }

      // Special case: Escape to close modals/help
      if (key === 'escape') {
        if (showHelp) {
          e.preventDefault();
          setShowHelp(false);
          return;
        }
      }

      const matchedShortcut = shortcuts.find(s => {
        const keyMatch = s.key.toLowerCase() === key;
        const ctrlMatch = !!s.ctrl === e.ctrlKey;
        const altMatch = !!s.alt === e.altKey;
        const shiftMatch = !!s.shift === e.shiftKey;
        return keyMatch && ctrlMatch && altMatch && shiftMatch;
      });

      if (matchedShortcut) {
        e.preventDefault();
        matchedShortcut.action();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts, showHelp]);

  return (
    <KeyboardShortcutsContext.Provider
      value={{ registerShortcut, unregisterShortcut, shortcuts, showHelp, setShowHelp }}
    >
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

export const useKeyboardShortcuts = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcuts must be used within a KeyboardShortcutsProvider');
  }
  return context;
};

// Hook for registering a shortcut
// Important: `action` is kept in a ref so we do NOT re-register on every parent render.
// Passing unstable callbacks in `deps` used to call registerShortcut → setShortcuts → infinite updates (React #185).
export const useShortcut = (shortcut: Omit<Shortcut, 'action'> & { action: () => void }, _deps: React.DependencyList = []) => {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const actionRef = useRef(shortcut.action);
  actionRef.current = shortcut.action;

  const { key, ctrl, alt, shift, description } = shortcut;

  useEffect(() => {
    const fullShortcut: Shortcut = {
      key,
      ctrl,
      alt,
      shift,
      description,
      action: () => actionRef.current(),
    };
    registerShortcut(fullShortcut);
    return () => unregisterShortcut(key);
  }, [key, ctrl, alt, shift, description, registerShortcut, unregisterShortcut]);
};

export default KeyboardShortcutsContext;
